import { useFormContext } from '@american-autonomy/ag-ui';

import FormGrid from 'components/FormGrid';
import ApplicationNameField from 'components/fields/ApplicationNameField';
import MultiSelectBatteryField from 'components/fields/MultiSelectBatteryField';
import MultiSelectDroneField from 'components/fields/MultiSelectDroneField';
import MultiSelectPilotField from 'components/fields/MultiSelectPilotField';
import SelectBoundaryField from 'components/fields/SelectBoundaryField';
import SelectChemicalMixField from 'components/fields/SelectChemicalMixField';
import SelectClientFarmFieldField from 'components/fields/SelectClientFarmFieldField';

import useField from 'hooks/useField';

import { useFieldBoundarySync } from './hooks/useFieldBoundarySync';
import useTranslation from './hooks/useTranslation';

import {
    APPLICATION_NAME_FIELD_NAME,
    BATTERIES_FIELD_NAME,
    BOUNDARY_ID_FIELD_NAME,
    CLIENT_FIELD_NAME,
    DRONES_FIELD_NAME,
    FARM_FIELD_NAME,
    FIELD_FIELD_NAME,
    MIX_FIELD_NAME,
    PILOT_IDS_FIELD_NAME,
    TEST_ID
} from './constants';
import type { ApplicationFormSchema, FieldPath, Props } from './types';

const ApplicationFormFields = <T extends ApplicationFormSchema>({
    children,
    namePrefix,
    testId = TEST_ID
}: Props<T>) => {
    const { control } = useFormContext<T>();
    const { APPLICATION_NAME_FIELD_PLACEHOLDER } = useTranslation();

    const fieldName = (name: keyof ApplicationFormSchema) =>
        (namePrefix ? `${namePrefix}.${name}` : String(name)) as FieldPath<T>;

    const { fetchFieldHierarchyFormItems } = useField();

    const handleFetchFieldHierarchy = (siteId: string) =>
        fetchFieldHierarchyFormItems({ id: siteId });

    const { siteId } = useFieldBoundarySync({
        boundaryPath: fieldName(BOUNDARY_ID_FIELD_NAME),
        clientPath: fieldName(CLIENT_FIELD_NAME),
        control,
        farmPath: fieldName(FARM_FIELD_NAME),
        fieldPath: fieldName(FIELD_FIELD_NAME),
        onFetchSiteHierarchy: handleFetchFieldHierarchy
    });

    return (
        <FormGrid testId={testId}>
            <ApplicationNameField<T>
                control={control}
                isRequired={true}
                name={fieldName(APPLICATION_NAME_FIELD_NAME)}
                placeholder={APPLICATION_NAME_FIELD_PLACEHOLDER}
            />

            <SelectClientFarmFieldField<T> namePrefix={namePrefix} />

            <MultiSelectDroneField<T> control={control} name={fieldName(DRONES_FIELD_NAME)} />

            <MultiSelectBatteryField<T> control={control} name={fieldName(BATTERIES_FIELD_NAME)} />

            <MultiSelectPilotField<T> control={control} name={fieldName(PILOT_IDS_FIELD_NAME)} />

            <SelectBoundaryField<T>
                control={control}
                name={fieldName(BOUNDARY_ID_FIELD_NAME)}
                siteId={siteId}
            />

            <SelectChemicalMixField<T> control={control} name={fieldName(MIX_FIELD_NAME)} />

            {children}
        </FormGrid>
    );
};

export default ApplicationFormFields;
